/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable no-underscore-dangle */
import loadOusterSDK from '@ouster/ouster-sdk-js-web/artifacts/ouster_js_web';
import { DataMessage } from '@ouster/webviz';

import { isMobileDevice } from '../hooks/useIsMobileViewport';
import { getOusterWasmBytes } from '../sdkWebPreload';
import {
  disablePthreadPool,
  resolveSdkPthreadBootstrapSource,
} from './sdkPthreadBootstrap';
import { createSdkStderrLogger } from './sdkLog';

let SDK: any = null;
let sdkInit: Promise<void> | null = null;
const autoExposureOnBySource = new Map<string, any>();
const autoExposureOffBySource = new Map<string, any>();
const warmedAutoExposureSources = new Set<string>();
let hdrRgbInputPtr = 0;
let hdrRgbOutputPtr = 0;
let hdrRgbAllocatedPixels = 0;
const WARMUP_PASSES = 5;

const cloneField = (raw: ArrayBufferView): ArrayBufferView => {
  if (raw instanceof Float64Array) return new Float32Array(raw);
  if (raw instanceof Float32Array) return new Float32Array(raw);
  if (raw instanceof Uint8Array) return new Uint8Array(raw);
  if (raw instanceof Uint16Array) return new Uint16Array(raw);
  if (raw instanceof Uint32Array) return new Uint32Array(raw);
  if (raw instanceof Int32Array) return new Int32Array(raw);
  const src = new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength);
  const out = new Uint8Array(src.length);
  out.set(src);
  return out;
};

const getMessageWidth = (msg: DataMessage): number => {
  const format = (msg.sensorInfo as any)?.format;
  const dataFormat = (msg.sensorInfo as any)?.dataFormat;
  return Number(format?.columns_per_frame ?? dataFormat?.columnsPerFrame ?? 0);
};

const getMessageHeight = (msg: DataMessage): number => {
  const format = (msg.sensorInfo as any)?.format;
  const dataFormat = (msg.sensorInfo as any)?.dataFormat;
  return Number(format?.pixels_per_column ?? dataFormat?.pixelsPerColumn ?? 0);
};

const isUsableCorrectedRgb = (values: Float32Array): boolean => {
  if (!values.length) return false;
  const sampleCount = Math.min(values.length, 96);
  const step = Math.max(1, Math.floor(values.length / sampleCount));
  for (let i = 0; i < values.length && i < sampleCount * step; i += step) {
    const value = values[i];
    if (!Number.isFinite(value) || Math.abs(value) > 1e8) {
      return false;
    }
  }
  return true;
};

const resolveSourceIdPart = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const getRgbAutoExposureSourceKey = (
  msg: DataMessage,
  channelName: string
): string => {
  const sensorInfo = (msg.sensorInfo as any) ?? {};
  const sourceId =
    resolveSourceIdPart(sensorInfo?.prodSn) ??
    resolveSourceIdPart(sensorInfo?.prod_sn) ??
    resolveSourceIdPart(sensorInfo?.serialNo) ??
    resolveSourceIdPart(sensorInfo?.serial_no) ??
    resolveSourceIdPart(sensorInfo?.sn) ??
    resolveSourceIdPart(sensorInfo?.hostname) ??
    resolveSourceIdPart(sensorInfo?.name) ??
    'unknown';
  return `${sourceId}:${channelName}`;
};

// Point cloud and 2D image panels each run their own auto-exposure pipeline
// over the same sensor/channel, but must not share AutoExposure state: an
// update triggered by one render target (e.g. toggling AE in the point
// cloud) must not perturb the gain the other render target is converging
// on. `stateKey` namespaces the corrector per render target so the two never
// collide even though they resolve the same sensor+channel source key.
const getRgbAutoExposureStateKey = (
  msg: DataMessage,
  channelName: string,
  stateKey?: string
): string => {
  const sourceKey = getRgbAutoExposureSourceKey(msg, channelName);
  return stateKey ? `${stateKey}:${sourceKey}` : sourceKey;
};

const getHdrRgbCorrector = (sourceKey: string, isAutoLevelsOn = true): any => {
  if (!SDK || typeof SDK.AutoExposure !== 'function') return null;
  if (!isAutoLevelsOn) {
    let corrector = autoExposureOffBySource.get(sourceKey);
    if (!corrector) {
      corrector = new SDK.AutoExposure(0, 0, 1, 0.9);
      autoExposureOffBySource.set(sourceKey, corrector);
    }
    return corrector;
  }
  let corrector = autoExposureOnBySource.get(sourceKey);
  if (!corrector) {
    corrector = new SDK.LocalToneMapper();
    autoExposureOnBySource.set(sourceKey, corrector);
  }
  return corrector;
};

const applyHdrRgbCorrection = (
  raw: ArrayBufferView,
  width: number,
  height: number,
  sourceKey: string,
  isAutoLevelsOn: boolean
): ArrayBufferView => {
  const corrector = getHdrRgbCorrector(sourceKey, isAutoLevelsOn);
  const W = Number(width);
  const H = Number(height);
  if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) {
    return cloneField(raw);
  }

  const pixels = W * H * 3;
  if (pixels <= 0) return cloneField(raw);

  const isUint16 = raw instanceof Uint16Array;
  const isUint32 = raw instanceof Uint32Array;
  const isFloat32 = raw instanceof Float32Array;
  if (!isUint16 && !isUint32 && !isFloat32) {
    return cloneField(raw);
  }

  if (raw.length !== pixels || !corrector) {
    return cloneField(raw);
  }

  try {
    const isFirstWarmupFrame =
      isAutoLevelsOn && !warmedAutoExposureSources.has(sourceKey);

    if (isFloat32) {
      const floatInput = new Float32Array(raw);
      const floatOutput = new Float32Array(pixels);
      if (isFirstWarmupFrame) {
        for (let i = 0; i < WARMUP_PASSES; i += 1) {
          corrector.update_float(floatInput, floatOutput, H, W, true);
        }
        warmedAutoExposureSources.add(sourceKey);
      } else {
        corrector.update_float(floatInput, floatOutput, H, W, true);
      }

      if (isUsableCorrectedRgb(floatOutput)) {
        return isFirstWarmupFrame
          ? Float32Array.from(floatOutput, (v) => (v < 0 ? 0 : v > 1 ? 1 : v))
          : floatOutput;
      }

      return cloneField(raw);
    }

    if (pixels > hdrRgbAllocatedPixels) {
      const inputBytes = pixels * 2;
      const outputBytes = pixels * 4;
      const newHdrRgbInputPtr = SDK._malloc(inputBytes);
      const newHdrRgbOutputPtr = SDK._malloc(outputBytes);

      if (!newHdrRgbInputPtr || !newHdrRgbOutputPtr) {
        if (newHdrRgbInputPtr) SDK._free(newHdrRgbInputPtr);
        if (newHdrRgbOutputPtr) SDK._free(newHdrRgbOutputPtr);
        return cloneField(raw);
      }

      if (hdrRgbInputPtr) SDK._free(hdrRgbInputPtr);
      if (hdrRgbOutputPtr) SDK._free(hdrRgbOutputPtr);

      hdrRgbInputPtr = newHdrRgbInputPtr;
      hdrRgbOutputPtr = newHdrRgbOutputPtr;
      hdrRgbAllocatedPixels = pixels;
    }

    const fp16Input = new Uint16Array(
      SDK.HEAPU8.buffer as ArrayBuffer,
      hdrRgbInputPtr,
      pixels
    );
    if (raw instanceof Uint16Array) {
      // raw is already integers in [0, 65535], so the trunc/clamp below is
      // the identity for every element - .set() is a memcpy of the same
      // bytes instead of ~1.5M scalar ops per frame on a 256x2048 RGB
      // sensor. Uint32 input still needs the loop, where the clamp is real.
      fp16Input.set(raw);
    } else {
      for (let i = 0; i < pixels; i += 1) {
        const value = Number(raw[i]);
        fp16Input[i] = Math.max(0, Math.min(65535, Math.trunc(value)));
      }
    }

    const fp16Output = new Float32Array(
      SDK.HEAPU8.buffer as ArrayBuffer,
      hdrRgbOutputPtr,
      pixels
    );

    // On first use after a reset, warm up a new AutoExposure instance by running
    // multiple passes before displaying the first frame.  With update_every=3, the gain
    // is not computed until the 3rd update_fp16 call; without warmup the first frames
    // would use an uninitialised gain.
    //
    // IMPORTANT: recreate views inside the warmup loop.  resetRgbAutoExposure()
    // clears per-source correctors, so a new source key gets a brand-new
    // AutoExposure instance.  Its very first update_fp16 call allocates
    // internal histogram
    // buffers inside WASM, growing the WASM heap.  That growth replaces
    // SDK.HEAPU8.buffer, detaching the fp16Input/fp16Output views created above.
    // Any subsequent use of those views (e.g. iteration 1+) throws
    // TypeError("Not WASM memory") → caught → Uint16 returned → PC colors wrong.
    // Recreating views each iteration from the current SDK.HEAPU8.buffer avoids
    // the error and guarantees the warmup always completes on the first call.
    if (isFirstWarmupFrame) {
      for (let i = 0; i < WARMUP_PASSES; i += 1) {
        corrector.update_fp16(
          new Uint16Array(
            SDK.HEAPU8.buffer as ArrayBuffer,
            hdrRgbInputPtr,
            pixels
          ),
          new Float32Array(
            SDK.HEAPU8.buffer as ArrayBuffer,
            hdrRgbOutputPtr,
            pixels
          ),
          H,
          W,
          true
        );
      }
      warmedAutoExposureSources.add(sourceKey);
    } else {
      corrector.update_fp16(fp16Input, fp16Output, H, W, true);
    }

    // Always read the result through a fresh view in case heap grew and
    // detached fp16Output during the (non-warmup) single update_fp16 call.
    const resultOutput = new Float32Array(
      SDK.HEAPU8.buffer as ArrayBuffer,
      hdrRgbOutputPtr,
      pixels
    );

    if (isUsableCorrectedRgb(resultOutput)) {
      // After warmup the gain was computed from a single frame, so isolated bright
      // pixels can still exceed 1.0 and render as solid white in ImageColorer.
      // Clamp only on this first-frame-after-reset path; all subsequent frames skip this branch.
      // The steady-state branch still has to copy out of the WASM heap so the
      // next frame's update_fp16 can't clobber it, but slice() gets there by
      // memcpy rather than Float32Array.from's generic iterator path.
      return isFirstWarmupFrame
        ? Float32Array.from(resultOutput, (v) => (v < 0 ? 0 : v > 1 ? 1 : v))
        : resultOutput.slice();
    }

    if (raw instanceof Uint32Array) {
      const floatInput = new Float32Array(pixels);
      for (let i = 0; i < pixels; i += 1) {
        floatInput[i] = raw[i];
      }
      const floatOutput = new Float32Array(pixels);
      corrector.update_float(floatInput, floatOutput, H, W, true);
      if (isUsableCorrectedRgb(floatOutput)) {
        return floatOutput;
      }
    }

    return cloneField(raw);
  } catch (error) {
    console.warn('HDR RGB correction failed in data-app main thread:', error);
    return cloneField(raw);
  }
};

export const preloadRgbAutoExposureSdk = async (): Promise<void> => {
  if (SDK) return;
  if (!sdkInit) {
    sdkInit = (async () => {
      // The SDK eagerly spawns its pthread worker pool, each loading
      // ouster_js_web.js standalone (separately from the copy bundled into
      // main.js). That standalone parse has been seen to fail with a raw,
      // uncaught SyntaxError (client-side network tooling mangling the
      // response in transit) even though the identical bundled copy runs
      // fine - suppress it here the same way worker failures are contained
      // elsewhere, instead of letting it leak to Sentry as an unhandled
      // window error.
      const onBootstrapWorkerError = (e: ErrorEvent) => {
        if (e.filename?.includes('ouster_js_web.js')) {
          e.preventDefault();
          console.error(
            '[rgb-sdk] pthread bootstrap script failed:',
            e.message
          );
        }
      };
      window.addEventListener('error', onBootstrapWorkerError);
      try {
        const wasmBinary = await getOusterWasmBytes();
        const { bootstrapUrl, source } =
          await resolveSdkPthreadBootstrapSource();
        // Run single-threaded instead of spawning a real pthread Worker
        // pool: those workers share WASM linear memory, which is far more
        // likely to hit an out-of-bounds trap in memory-constrained mobile
        // in-app browsers (Twitter/Instagram/etc.'s embedded WebViews).
        // Desktop browsers aren't memory-constrained the same way, so let
        // them use the full pthread pool for the performance benefit.
        const patchedSource = isMobileDevice()
          ? disablePthreadPool(source)
          : source;
        const mainScriptUrlOrBlob =
          patchedSource === source
            ? bootstrapUrl
            : new Blob([patchedSource], { type: 'application/javascript' });
        const sdk = await loadOusterSDK({
          wasmBinary,
          mainScriptUrlOrBlob,
          onAbort: (reason: unknown) => {
            console.error('[rgb-sdk] onAbort', reason);
          },
          printErr: createSdkStderrLogger('[rgb-sdk]'),
        });
        if (
          sdk?.pthreadPoolReady &&
          typeof sdk.pthreadPoolReady.then === 'function'
        ) {
          await sdk.pthreadPoolReady;
        }
        SDK = sdk;
      } finally {
        window.removeEventListener('error', onBootstrapWorkerError);
      }
    })();
  }
  await sdkInit;
};

const RAW_RGB_SUFFIX = ' RAW';

// SDK.AutoExposure is an Embind-wrapped C++ object: dropping the JS
// reference does NOT free its underlying WASM heap allocations (histogram
// buffers). It must be explicitly `.delete()`-d or every reset (new file,
// tab switch, live sensor select) permanently leaks one instance per
// source key that was ever touched this session.
const deleteCorrector = (corrector: unknown): void => {
  if (
    corrector &&
    typeof (corrector as { delete?: unknown }).delete === 'function' &&
    typeof (corrector as { isDeleted?: unknown }).isDeleted === 'function' &&
    !(corrector as { isDeleted: () => boolean }).isDeleted()
  ) {
    (corrector as { delete: () => void }).delete();
  }
};

/**
 * Reset the auto-exposure state so that the next recording starts fresh.
 * Call this whenever a new file / session is opened, before any frames
 * are processed.  The first frame for each source key runs WARMUP_PASSES
 * update iterations so the gain is computed before display.
 */
export const resetRgbAutoExposure = (): void => {
  autoExposureOnBySource.forEach(deleteCorrector);
  autoExposureOffBySource.forEach(deleteCorrector);
  autoExposureOnBySource.clear();
  autoExposureOffBySource.clear();
  warmedAutoExposureSources.clear();
};

export const applyRgbAutoExposure = (
  msg: DataMessage,
  channelName: string,
  isAutoLevelsOn: boolean,
  stateKey?: string
): DataMessage => {
  if (
    !msg.rgbChannels?.includes(channelName) ||
    channelName.endsWith(RAW_RGB_SUFFIX)
  ) {
    return msg;
  }

  const raw = msg.channelFields[channelName];
  if (!raw) {
    return msg;
  }

  if (!SDK) {
    if (!sdkInit) {
      preloadRgbAutoExposureSdk().catch((error) => {
        console.error('Failed to initialize RGB auto exposure SDK:', error);
      });
    }
    // SDK not yet loaded.  Returning the raw fp16-encoded Uint16Array would
    // cause ImageColorer to interpret integer-scale values as floats → solid
    // white.  Substitute a zero Float32Array (black image) so the panel shows
    // dark instead of white while the WASM initialises (~1 s on first load).
    const n = getMessageWidth(msg) * getMessageHeight(msg) * 3;
    if (n > 0) {
      return {
        ...msg,
        channelFields: {
          ...msg.channelFields,
          [channelName]: new Float32Array(n),
        },
      };
    }
    return msg;
  }

  return {
    ...msg,
    channelFields: {
      ...msg.channelFields,
      [channelName]: applyHdrRgbCorrection(
        raw,
        getMessageWidth(msg),
        getMessageHeight(msg),
        getRgbAutoExposureStateKey(msg, channelName, stateKey),
        isAutoLevelsOn
      ) as unknown as (typeof msg.channelFields)[string],
    },
  };
};
